import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
 ChangeDetectionStrategy,
} from '@angular/core';

const PAGE_SIZE = 20;

export interface DfSelectMultipleOption {
  VALUE: string | number;
  LABEL: string;
  isChecked?: boolean;
  [key: string]: unknown;
}

export interface DfSelectMultipleFilter {
  label?: string;
  loading?: boolean;
  values?: DfSelectMultipleOption[];
  selected: DfSelectMultipleOption[];
  param_name?: string;
  [key: string]: unknown;
}

/**
 * Angular 12 migration of AngularJS dfSelectMultiple directive.
 * Multi-select dropdown with Check All / UnCheck All, search, and lazy-loaded list.
 * Single file: directive (component) with inline template and styles.
 */
@Component({
    selector: 'gtw-df-select-multiple, [df-select-multiple]',
    template: `
    @if (filter?.loading) {
<div class="gt-filter-loading">
      <i class="fa fa-spinner fa-spin"></i>
    </div>
}
    @if (!filter?.loading) {
<div class="multiselect-parent btn-group dropdown-multiselect">
      <button class="dropdown-toggle btn btn-default btn-filter btn-sm" (click)="toggleDropdown()" [attr.aria-expanded]="isOpen" type="button">
        @if ((filter?.selected?.length ?? 0) > 0) {
<span>{{ filter?.selected?.length ?? 0 }} checked</span>
}
        @if ((filter?.selected?.length ?? 0) === 0) {
<span>{{ filterLabel }}</span>
}
        &nbsp;<span class="caret"></span>
      </button>
      <ul class="dropdown-menu dropdown-menu-form" [class.show]="isOpen" #scrollContainer (scroll)="onListScroll($event)">
        <li (click)="dfCheckAll($event)"><a tabindex="-1"><i class="fa fa-check action-icon"></i> Check All</a></li>
        <li (click)="dfUnCheckAll($event)"><a tabindex="-1"><i class="fa fa-times action-icon"></i> Uncheck All</a></li>
        <li class="divider"></li>
        <li class="search-li">
          <div class="dropdown-header">
            <input type="text" class="form-control searchField" placeholder="Search..." [(ngModel)]="searchItem" (ngModelChange)="dfSearching($event)" />
          </div>
        </li>
        <li class="divider"></li>
        @for (filterList of filterCachedDrpList; track filterList; let i = $index) {
<li
         
          (click)="dfSelectedListItem(filterList, i, $event, filterList.isChecked ?? false)"
        >
          <a class="item-link">@if (filterList.isChecked === true) {
<i class="fa fa-check"></i>
} {{ filterList.LABEL }}</a>
        </li>
}
        @if ((filterCachedDrpList?.length ?? 0) === 0 && searchValue.length > 0) {
<li class="df-no-results">
          <a><i>No search results found for "{{ searchItem }}"</i></a>
        </li>
}
      </ul>
    </div>
}
  `,
    styles: [`
    :host { display: inline-block; }
    .gt-filter-loading { min-height: 28px; display: flex; align-items: center; }
    .gt-filter-loading i { font-size: 1.2rem; }
    .multiselect-parent { position: relative; display: inline-block; }
    .dropdown-toggle.btn-filter.btn-sm {
      display: inline-block; cursor: pointer;
      padding: 5px 8px;
      border: 1px solid #c25423; border-radius: 3px;
      background: linear-gradient(to left, #c25423 0%, #c25423 25px, #fff 25px, #fff 100%);
      height: 25px; font-size: 12px;
      color: #000; line-height: 6px;
      text-align: center;
      box-shadow: inset 0 3px 5px rgba(0,0,0,0.125);
      white-space: nowrap;
    }
    .dropdown-toggle.btn-filter.btn-sm .caret {
      display: inline-block;
      width: 0; height: 0;
      margin-left: 30px;
      vertical-align: middle;
      border-top: 4px dashed;
      border-right: 4px solid transparent;
      border-left: 4px solid transparent;
      color: #fff;
    }
    .dropdown-menu.dropdown-menu-form {
      position: absolute; top: 100%; left: 0; z-index: 1000;
      min-width: 160px; width: 160px; height: 400px;
      overflow: auto; display: none;
      border: 1px solid rgba(0,0,0,0.15); border-radius: 4px;
      box-shadow: 0 6px 12px rgba(0,0,0,0.176);
      padding: 5px 0; margin: 2px 0 0;
      background: #fff; color: #333;
      list-style: none;
      font-size: 14px; line-height: 20px;
    }
    .dropdown-menu.dropdown-menu-form.show { display: block; }
    .dropdown-menu li { position: relative; cursor: pointer; padding: 0; margin: 0; }
    .dropdown-menu li a {
      display: block; padding: 1px 20px;
      color: #333; font-size: 14px; line-height: 20px;
      text-decoration: none; white-space: nowrap; cursor: pointer;
    }
    .dropdown-menu li a:hover { background: #f5f5f5; }
    .dropdown-menu li a .action-icon { font-size: 14px; line-height: 14px; margin-right: 4px; }
    .dropdown-menu li a .fa-check { color: #333; font-size: 14px; margin-right: 4px; }
    .dropdown-menu li.divider {
      height: 1px; margin: 1px 0; padding: 0;
      overflow: hidden; background-color: #95a4a4;
    }
    .dropdown-menu li.search-li { cursor: default; }
    .dropdown-menu li.search-li a:hover { background: none; }
    .dropdown-header { padding: 3px 20px; }
    .dropdown-header .form-control.searchField {
      width: 100%; height: 34px; font-size: 14px;
      padding: 6px 12px; border-radius: 4px;
      border: 1px solid #ccc;
    }
    .dropdown-header .form-control.searchField:focus {
      border-color: #66afe9;
      box-shadow: inset 0 1px 1px rgba(0,0,0,0.075), 0 0 8px rgba(102,175,233,0.6);
    }
    .df-no-results a { font-size: 14px; color: #666; }
  `],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DfSelectMultipleDirective implements OnChanges, AfterViewInit {
  @Input() filter!: DfSelectMultipleFilter;
  @Output() filterChange = new EventEmitter<DfSelectMultipleFilter>();

  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLUListElement>;

  searchItem = '';
  searchValue = '';
  filterLabel = '';
  filterTotalDrpList: DfSelectMultipleOption[] = [];
  filterCachedDrpList: DfSelectMultipleOption[] = [];
  searchResults: DfSelectMultipleOption[] = [];
  isOpen = false;
  private listIndx = 0;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const f = changes['filter']?.currentValue as DfSelectMultipleFilter | undefined;
    if (f) {
      this.filterLabel = f.label ?? '';
      this.syncFromFilter(f);
    }
  }

  ngAfterViewInit(): void {}

  private syncFromFilter(f: DfSelectMultipleFilter): void {
    if (!f) return;
    if (!Array.isArray(f.selected)) f.selected = [];
    const values = f.values ?? [];
    this.filterTotalDrpList = values.map((v) => ({ ...v }));
    this.filterCachedDrpList = this.filterTotalDrpList.slice(0, PAGE_SIZE);
    this.searchItem = '';
    this.searchValue = '';
    this.listIndx = 0;
    f.selected.forEach((sel) => {
      const item = this.filterTotalDrpList.find((y) => y.VALUE === sel.VALUE);
      if (item) item.isChecked = true;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.isOpen && target && !this.elementRef.nativeElement.contains(target)) this.isOpen = false;
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  resetList(): void {
    this.listIndx = 0;
    this.filterCachedDrpList = this.filterTotalDrpList.slice(0, PAGE_SIZE);
  }

  dfSearching(value: string | unknown): void {
    this.searchValue = (typeof value === 'string' ? value : '').toLowerCase();
    if (this.searchValue.length > 0) {
      this.searchResults = this.filterTotalDrpList.filter((itr) =>
        (itr.LABEL ?? '').toString().toLowerCase().indexOf(this.searchValue) !== -1
      );
      this.filterCachedDrpList = this.searchResults.slice(0, PAGE_SIZE);
    } else {
      this.resetList();
    }
  }

  onListScroll(_event: Event): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 4) this.fetchMoreDfSelectList();
  }

  fetchMoreDfSelectList(): void {
    this.listIndx = this.filterCachedDrpList.length;
    const r = PAGE_SIZE;
    if (this.searchValue.length > 0) {
      const next = this.searchResults.slice(this.listIndx, this.listIndx + r);
      this.filterCachedDrpList = [...this.filterCachedDrpList, ...next];
    } else {
      const next = this.filterTotalDrpList.slice(this.listIndx, this.listIndx + r);
      this.filterCachedDrpList = [...this.filterCachedDrpList, ...next];
    }
  }

  dfSelectedListItem(selectedDfList: DfSelectMultipleOption, index: number, event: Event, checkedFlag: boolean): void {
    event.stopPropagation();
    if (checkedFlag === true) {
      this.filterCachedDrpList[index].isChecked = false;
    } else {
      this.filterCachedDrpList[index].isChecked = true;
    }
    this.filterCachedDrpList.forEach((item) => {
      if (item.isChecked === true) {
        if (!this.filter.selected.some((s) => s.VALUE === item.VALUE)) this.filter.selected.push(item);
      } else {
        const idx = this.filter.selected.findIndex((s) => s.VALUE === item.VALUE);
        if (idx !== -1) this.filter.selected.splice(idx, 1);
      }
    });
    this.filterChange.emit(this.filter);
  }

  dfUnCheckAll(event: Event): void {
    event.stopPropagation();
    this.filterTotalDrpList.forEach((eachItem) => { if (eachItem.isChecked) eachItem.isChecked = false; });
    this.filter.selected = [];
    this.filterChange.emit(this.filter);
  }

  dfCheckAll(event: Event): void {
    event.stopPropagation();
    this.filterTotalDrpList.forEach((eachItem) => {
      if (eachItem.isChecked !== true) {
        eachItem.isChecked = true;
        this.filter.selected.push(eachItem);
      }
    });
    this.filterChange.emit(this.filter);
  }

  resetDropdownState(): void {
    this.filter.selected = [];
    this.filterTotalDrpList?.forEach((item) => (item.isChecked = false));
    this.resetList();
  }
}
